import React, { useState, useEffect, useCallback } from "react";
import {
  MagnifyingGlassIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  FunnelIcon,
  EyeIcon,
  TrashIcon,
  ChatBubbleLeftRightIcon,
  CheckCircleIcon,
  XMarkIcon,
  ExclamationTriangleIcon,
  ClockIcon,
  UserIcon,
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import {
  getSupportTickets,
  updateSupportTicket,
  deleteSupportTicket,
  getSupportTicketById,
  addNoteToTicket,
} from "../api/supportTickets";
import debounce from "lodash/debounce";

const SupportTickets = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [limit] = useState(20);
  const [filters, setFilters] = useState({
    status: "",
    priority: "",
    category: "",
    isBot: "",
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [newNote, setNewNote] = useState("");
  const [addingNote, setAddingNote] = useState(false);

  const statusOptions = [
    { value: "", label: "All Status" },
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In Progress" },
    { value: "resolved", label: "Resolved" },
    { value: "closed", label: "Closed" },
  ];

  const priorityOptions = [
    { value: "", label: "All Priority" },
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "urgent", label: "Urgent" },
  ];

  const categoryOptions = [
    { value: "", label: "All Categories" },
    { value: "general", label: "General" },
    { value: "technical", label: "Technical" },
    { value: "billing", label: "Billing" },
    { value: "feature_request", label: "Feature Request" },
    { value: "bug_report", label: "Bug Report" },
    { value: "other", label: "Other" },
  ];

  const botOptions = [
    { value: "", label: "All" },
    { value: "false", label: "Human" },
    { value: "true", label: "Bot" },
  ];

  const getStatusIcon = (status) => {
    switch (status) {
      case "open":
        return <ExclamationTriangleIcon className="h-4 w-4 text-red-500" />;
      case "in_progress":
        return <ClockIcon className="h-4 w-4 text-yellow-500" />;
      case "resolved":
        return <CheckCircleIcon className="h-4 w-4 text-green-500" />;
      case "closed":
        return <XMarkIcon className="h-4 w-4 text-gray-500" />;
      default:
        return <ExclamationTriangleIcon className="h-4 w-4 text-gray-500" />;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case "low":
        return "bg-gray-100 text-gray-800";
      case "medium":
        return "bg-blue-100 text-blue-800";
      case "high":
        return "bg-orange-100 text-orange-800";
      case "urgent":
        return "bg-red-100 text-red-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "open":
        return "bg-red-100 text-red-800";
      case "in_progress":
        return "bg-yellow-100 text-yellow-800";
      case "resolved":
        return "bg-green-100 text-green-800";
      case "closed":
        return "bg-gray-100 text-gray-800";
      case "unknown":
        return "bg-gray-100 text-gray-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const fetchTickets = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        limit,
        search,
        sortBy,
        sortOrder,
        ...filters,
      };

      // Remove empty filter values
      Object.keys(params).forEach((key) => {
        if (
          params[key] === "" ||
          params[key] === null ||
          params[key] === undefined
        ) {
          delete params[key];
        }
      });

      const response = await getSupportTickets(params);

      setTickets(response.data.tickets || []);
      setTotalPages(response.data.pagination.totalPages);
      setTotalItems(response.data.pagination.totalTickets);
    } catch (error) {
      toast.error(error.message || "Failed to fetch support tickets");
    } finally {
      setLoading(false);
    }
  };

  const debouncedFetchTickets = useCallback(
    debounce(() => {
      fetchTickets();
    }, 500),
    [currentPage, search, sortBy, sortOrder, filters]
  );

  useEffect(() => {
    debouncedFetchTickets();
    return () => {
      debouncedFetchTickets.cancel();
    };
  }, [currentPage, search, sortBy, sortOrder, filters]);

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortOrder("asc");
    }
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = (filterName, value) => {
    setFilters((prev) => ({
      ...prev,
      [filterName]: value,
    }));
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "",
      priority: "",
      category: "",
      isBot: "",
    });
    setSearch("");
    setCurrentPage(1);
  };

  const handleDelete = async (ticketId) => {
    if (window.confirm("Are you sure you want to delete this ticket?")) {
      try {
        await deleteSupportTicket(ticketId);
        toast.success("Ticket deleted successfully");
        fetchTickets();
      } catch (error) {
        toast.error(error.message || "Failed to delete ticket");
      }
    }
  };

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      await updateSupportTicket(ticketId, { status: newStatus });
      toast.success("Ticket status updated successfully");
      fetchTickets();
    } catch (error) {
      toast.error(error.message || "Failed to update ticket status");
    }
  };

  const handleViewDetails = async (ticketId) => {
    try {
      console.log("Fetching ticket details for:", ticketId);
      const response = await getSupportTicketById(ticketId);
      console.log("API Response:", response);
      // Handle both possible response structures
      const ticketData = response.ticket || response;
      console.log("Ticket Data:", ticketData);
      setSelectedTicket(ticketData);
      setIsDetailModalOpen(true);
    } catch (error) {
      console.error("Error fetching ticket details:", error);
      toast.error(error.message || "Failed to fetch ticket details");
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) {
      toast.error("Please enter a note");
      return;
    }

    try {
      setAddingNote(true);
      await addNoteToTicket(selectedTicket._id, { note: newNote });
      toast.success("Note added successfully");
      setNewNote("");

      // Refresh ticket details
      const response = await getSupportTicketById(selectedTicket._id);
      const ticketData = response.ticket || response;
      setSelectedTicket(ticketData);
      setIsDetailModalOpen(false);
    } catch (error) {
      toast.error(error.message || "Failed to add note");
    } finally {
      setAddingNote(false);
    }
  };

  const SortIcon = ({ column }) => {
    if (sortBy !== column) return null;
    return sortOrder === "asc" ? (
      <ChevronUpIcon className="h-4 w-4 inline-block ml-1" />
    ) : (
      <ChevronDownIcon className="h-4 w-4 inline-block ml-1" />
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Support Tickets</h1>
        <div className="flex space-x-2">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 flex items-center space-x-2"
          >
            <FunnelIcon className="h-5 w-5" />
            <span>Filters</span>
          </button>
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="mb-6 bg-white p-4 rounded-lg shadow">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filters.status}
                onChange={(e) => handleFilterChange("status", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Priority
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange("priority", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {priorityOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={filters.category}
                onChange={(e) => handleFilterChange("category", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {categoryOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={filters.isBot}
                onChange={(e) => handleFilterChange("isBot", e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                {botOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                onClick={clearFilters}
                className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search tickets by name, email, subject, or message..."
            value={search}
            onChange={handleSearch}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
          />
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
        </div>
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("createdAt")}
                >
                  Date <SortIcon column="createdAt" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Customer
                </th>
                <th
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer"
                  onClick={() => handleSort("subject")}
                >
                  Subject <SortIcon column="subject" />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Priority
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Category
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assigned To
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    Loading...
                  </td>
                </tr>
              ) : tickets.length === 0 ? (
                <tr>
                  <td
                    colSpan="10"
                    className="px-6 py-4 text-center text-gray-500"
                  >
                    No tickets found
                  </td>
                </tr>
              ) : (
                tickets.map((ticket) => (
                  <tr key={ticket._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {formatDate(ticket.createdAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-mono text-gray-900">
                        {ticket.ticketId || ticket._id}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center">
                            <UserIcon className="h-5 w-5 text-gray-600" />
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {ticket.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {ticket.email}
                          </div>
                          {ticket.company && (
                            <div className="text-xs text-gray-400">
                              {ticket.company}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 max-w-xs truncate">
                        {ticket.subject}
                      </div>
                      <div className="text-sm text-gray-500 max-w-xs truncate">
                        {ticket.message}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(ticket.status)}
                        <span
                          className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                            ticket.status || "unknown"
                          )}`}
                        >
                          {(ticket.status || "unknown").replace("_", " ")}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                          ticket.priority || "low"
                        )}`}
                      >
                        {ticket.priority || "low"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {(ticket.category || "general").replace("_", " ")}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {ticket.assignedTo ? (
                          <div>
                            <div className="font-medium">
                              {ticket.assignedTo.name}
                            </div>
                            <div className="text-gray-500">
                              {ticket.assignedTo.email}
                            </div>
                          </div>
                        ) : (
                          <span className="text-gray-400">Unassigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {ticket.isBot ? (
                          <span className="text-red-600 font-medium">Bot</span>
                        ) : (
                          <span className="text-green-600 font-medium">
                            Human
                          </span>
                        )}
                      </div>
                      {ticket.botScore > 0 && (
                        <div className="text-xs text-gray-500">
                          Score: {ticket.botScore}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleViewDetails(ticket._id)}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-gray-600 hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleStatusUpdate(ticket._id, "in_progress")
                          }
                          disabled={ticket.status === "in_progress"}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mark In Progress"
                        >
                          <ClockIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() =>
                            handleStatusUpdate(ticket._id, "resolved")
                          }
                          disabled={ticket.status === "resolved"}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Mark Resolved"
                        >
                          <CheckCircleIcon className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ticket._id)}
                          className="inline-flex items-center p-2 border border-transparent rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                          title="Delete Ticket"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
          <div className="flex-1 flex justify-between sm:hidden">
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages}
              className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
            <div>
              <p className="text-sm text-gray-700">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * limit + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * limit, totalItems)}
                </span>{" "}
                of <span className="font-medium">{totalItems}</span> results
              </p>
            </div>
            <div>
              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => setCurrentPage(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                {[...Array(totalPages)].map((_, index) => (
                  <button
                    key={index + 1}
                    onClick={() => setCurrentPage(index + 1)}
                    className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                      currentPage === index + 1
                        ? "z-10 bg-primary border-primary text-white"
                        : "bg-white border-gray-300 text-gray-500 hover:bg-gray-50"
                    }`}
                  >
                    {index + 1}
                  </button>
                ))}
                <button
                  onClick={() => setCurrentPage(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Ticket Detail Modal */}
      {isDetailModalOpen && selectedTicket && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4 text-center sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />

            <div className="relative inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                      Ticket Details -{" "}
                      {selectedTicket.ticketId || selectedTicket._id}
                    </h3>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {/* Customer Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">
                          Customer Information
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Name:
                            </span>
                            <span className="ml-2 text-sm text-gray-900">
                              {selectedTicket.name}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Email:
                            </span>
                            <span className="ml-2 text-sm text-gray-900">
                              {selectedTicket.email}
                            </span>
                          </div>
                          {selectedTicket.company && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">
                                Company:
                              </span>
                              <span className="ml-2 text-sm text-gray-900">
                                {selectedTicket.company}
                              </span>
                            </div>
                          )}
                          {selectedTicket.phone && (
                            <div>
                              <span className="text-sm font-medium text-gray-500">
                                Phone:
                              </span>
                              <span className="ml-2 text-sm text-gray-900">
                                {selectedTicket.phone}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Ticket Information */}
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <h4 className="font-medium text-gray-900 mb-3">
                          Ticket Information
                        </h4>
                        <div className="space-y-2">
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Status:
                            </span>
                            <span
                              className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(
                                selectedTicket.status || "unknown"
                              )}`}
                            >
                              {(selectedTicket.status || "unknown").replace(
                                "_",
                                " "
                              )}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Priority:
                            </span>
                            <span
                              className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(
                                selectedTicket.priority || "low"
                              )}`}
                            >
                              {selectedTicket.priority || "low"}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Category:
                            </span>
                            <span className="ml-2 text-sm text-gray-900">
                              {(selectedTicket.category || "general").replace(
                                "_",
                                " "
                              )}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Type:
                            </span>
                            <span
                              className={`ml-2 text-sm font-medium ${
                                selectedTicket.isBot
                                  ? "text-red-600"
                                  : "text-green-600"
                              }`}
                            >
                              {selectedTicket.isBot ? "Bot" : "Human"}
                            </span>
                          </div>
                          <div>
                            <span className="text-sm font-medium text-gray-500">
                              Created:
                            </span>
                            <span className="ml-2 text-sm text-gray-900">
                              {formatDate(selectedTicket.createdAt)}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Subject and Message */}
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Subject
                      </h4>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg">
                        {selectedTicket.subject}
                      </p>
                    </div>

                    <div className="mt-4">
                      <h4 className="font-medium text-gray-900 mb-3">
                        Message
                      </h4>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                        {selectedTicket.message}
                      </p>
                    </div>

                    {/* Assigned To */}
                    {selectedTicket.assignedTo && (
                      <div className="mt-4">
                        <h4 className="font-medium text-gray-900 mb-3">
                          Assigned To
                        </h4>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="text-sm font-medium text-gray-900">
                            {selectedTicket.assignedTo.name}
                          </div>
                          <div className="text-sm text-gray-500">
                            {selectedTicket.assignedTo.email}
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Notes Section */}
                    <div className="mt-6">
                      <h4 className="font-medium text-gray-900 mb-3">Notes</h4>

                      {/* Add Note Form */}
                      <div className="mb-4">
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Add a note about this ticket..."
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                          rows={3}
                        />
                        <div className="mt-2 flex justify-end">
                          <button
                            onClick={handleAddNote}
                            disabled={addingNote || !newNote.trim()}
                            className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {addingNote ? "Adding..." : "Add Note"}
                          </button>
                        </div>
                      </div>

                      {/* Display Notes */}
                      {selectedTicket.internalNotes &&
                      selectedTicket.internalNotes.length > 0 ? (
                        <div className="space-y-3">
                          {selectedTicket.internalNotes.map((note, index) => (
                            <div
                              key={index}
                              className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400"
                            >
                              <div className="text-sm text-gray-900">
                                {note.note}
                              </div>
                              {note.addedAt && (
                                <div className="text-xs text-gray-500 mt-1">
                                  {formatDate(note.addedAt)}
                                </div>
                              )}
                              {note.addedBy && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Added by: {note?.addedBy?.name}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500 italic">
                          No notes yet
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => {
                    setIsDetailModalOpen(false);
                    setSelectedTicket(null);
                    setNewNote("");
                  }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary text-base font-medium text-white hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SupportTickets;
